import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormHelperText,
    Grid,
    MenuItem,
    Radio,
    Select,
    Switch,
    TextField,
    Tooltip,
} from "@mui/material/";
import { updateAppState } from "../../actions/appstate";
import { SYSTEM_SETTINGS_UPDATED } from "../../actions/types";
import dataService from "../../services/data.service";
import { userReadyOnlyStatus, getPermissionMessage, getPermissions } from "../../app/util";
import useUserLoginType from "../../hooks/useUserLoginType";

const CURRENCY_OPTIONS = [
    "AED", "AFN", "ALL", "AMD", "ANG", "AOA", "ARS", "AUD", "AWG", "AZN",
    "BAM", "BBD", "BDT", "BGN", "BHD", "BND", "BOB", "BRL", "BSD", "BTN",
    "BWP", "BYR", "BZD", "CAD", "CHF", "CLP", "CNY", "COP", "CRC", "CZK",
    "DKK", "DOP", "DZD", "EGP", "ETB", "EUR", "FJD", "GBP", "GEL", "GHS",
    "GMD", "GTQ", "GYD", "HKD", "HNL", "HRK", "HUF", "IDR", "ILS", "INR",
    "ISK", "JEP", "JMD", "JOD", "JPY", "KES", "KGS", "KHR", "KRW", "KWD",
    "KYD", "KZT", "LBP", "LKR", "LTL", "LVL", "MAD", "MDL", "MGA", "MKD",
    "MMK", "MNT", "MOP", "MUR", "MVR", "MXN", "MYR", "MZN", "NAD", "NGN",
    "NIO", "NOK", "NPR", "NZD", "OMR", "PEN", "PGK", "PHP", "PKR", "PLN",
    "PYG", "QAR", "RON", "RSD", "RUB", "RWF", "SAR", "SCR", "SEK", "SGD",
    "STD", "SYP", "THB", "TND", "TRY", "TTD", "TWD", "TZS", "UAH", "UGX",
    "USD", "UYU", "VEF", "VND", "VUV", "WST", "XAF", "XBT", "XCD", "XOF",
    "XPF", "ZAR", "ZMW",
];

const FREQUENCY_OPTIONS = ["Weekly", "Bi-Weekly", "Monthly"];

const EMPTY_STATE = {
    subscription_id: "",
    subscription_property_id: "",
    currency_code: "",
    delivery_frequency: "",
    comp_sets: [],
};

function AmadeusSettings(props) {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { app, auth } = useSelector((state) => state);
    const { app_state, ui_state } = app.current_state;
    const { user } = auth;
    const { property_details } = auth.profile || [];
    const { permissions } = getPermissions(property_details, app_state.property_id);
    let prop_details_index = 0;
    property_details.forEach((item, index) => {
        if (item.id === app_state.property_id) prop_details_index = index;
    });

    const userLoginType = useUserLoginType();
    const usrReadyOnlyStatus = userReadyOnlyStatus(userLoginType);

    const [isShakeButton, setIsShakeButton] = useState(false);
    const [isrequesting, setIsRequesting] = useState(false);
    const [isRequestSuccess, setIsRequestSuccess] = useState(false);
    const [isRequestError, setIsRequestError] = useState(false);
    const [isDuplicateWarning, setIsDuplicateWarning] = useState(false);
    const [resErrorMsg, setResErrorMsg] = useState("");
    const [requestData, setRequestData] = useState(JSON.parse(JSON.stringify(EMPTY_STATE)));
    const [submitAttempted, setSubmitAttempted] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const savedDataRef = useRef(JSON.parse(JSON.stringify(EMPTY_STATE)));
    const isDirty = JSON.stringify(requestData) !== JSON.stringify(savedDataRef.current);
    localStorage.setItem("isChanged", isDirty);

    const hasErrors =
        !String(requestData.subscription_id || "").trim() ||
        !String(requestData.subscription_property_id || "").trim() ||
        !String(requestData.currency_code || "").trim() ||
        requestData.comp_sets.length === 0 ||
        requestData.comp_sets.some(row => !String(row.comp_set_id || "").trim());

    const getFieldError = (value) => submitAttempted && !String(value || "").trim();
    const getCompSetIdError = (value) => submitAttempted && !String(value || "").trim();

    useEffect(() => {
        setIsLoading(true);
        setRequestData(JSON.parse(JSON.stringify(EMPTY_STATE)));
        setSubmitAttempted(false);
        dataService
            .getData({
                api: `rms/properties/benchmarking/amadeus/${app_state.property_id}/`,
                request: { method: "GET" },
            })
            .then((data) => {
                if (data.status === "success" && data.data) {
                    const sorted = {
                        ...data.data,
                        subscription_id: data.data.subscription_id || "",
                        subscription_property_id: data.data.subscription_property_id || "",
                        currency_code: data.data.currency_code || "",
                        delivery_frequency: data.data.delivery_frequency || "",
                        comp_sets: [...(data.data.comp_sets || [])].sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0)),
                    };
                    savedDataRef.current = JSON.parse(JSON.stringify(sorted));
                    setRequestData(sorted);
                } else {
                    savedDataRef.current = JSON.parse(JSON.stringify(EMPTY_STATE));
                }
            })
            .catch(() => {
                savedDataRef.current = JSON.parse(JSON.stringify(EMPTY_STATE));
            })
            .finally(() => {
                setIsLoading(false);
            });
    }, [app_state.property_id]);

    function goBackToDashboard() {
        dispatch(
            updateAppState("navigate_to_dashboard", {
                app_state: { data_load: true, kpi_load: true },
                ui_state: { active_settings_menu: "", showspinner: true },
            }, "settings_click")
        );
        navigate("/");
    }

    const cancelSave = () => {
        setRequestData(JSON.parse(JSON.stringify(savedDataRef.current)));
        setSubmitAttempted(false);
    };

    const doSaveApi = () => {
        setIsDuplicateWarning(false);
        setIsRequesting(true);

        const { property_id: _pid, ...payload } = requestData;
        payload.comp_sets = payload.comp_sets.map(({ isNew: _, ...rest }) => rest);

        dataService
            .update({
                api: `rms/properties/benchmarking/amadeus/${app_state.property_id}/`,
                request: { method: "PUT", ...payload },
            })
            .then((data) => {
                setIsRequesting(false);
                if (data.status === "success") {
                    const sorted = {
                        ...requestData,
                        comp_sets: [...requestData.comp_sets]
                            .sort((a, b) => (b.is_primary ? 1 : 0) - (a.is_primary ? 1 : 0))
                            .map(({ isNew: _, ...rest }) => rest),
                    };
                    savedDataRef.current = JSON.parse(JSON.stringify(sorted));
                    setRequestData(sorted);
                    if (!permissions?.benchmarking?.amadeus_demand360) {
                        const profile = auth.profile;
                        profile.property_details[prop_details_index].permissions.benchmarking.amadeus_demand360 = true;
                        dispatch({ type: SYSTEM_SETTINGS_UPDATED, payload: profile });
                    }
                    setIsRequestSuccess(true);
                    setSubmitAttempted(false);
                    setTimeout(() => setIsRequestSuccess(false), 5000);
                } else {
                    setIsRequestError(true);
                    setResErrorMsg(data.message || "Something went wrong, please try again.");
                    setTimeout(() => setIsRequestError(false), 5000);
                }
            })
            .catch((err) => {
                setIsRequesting(false);
                setIsRequestError(true);
                setResErrorMsg(
                    (err && err.original && err.original.statusText) ||
                    "Something went wrong, please try again later!"
                );
                setTimeout(() => setIsRequestError(false), 5000);
            });
    };

    const saveChanges = () => {
        setSubmitAttempted(true);
        if (hasErrors) return;

        const ids = requestData.comp_sets.map(r => String(r.comp_set_id || "").trim()).filter(Boolean);
        const hasDuplicates = ids.length !== new Set(ids).size;

        if (hasDuplicates) {
            setIsDuplicateWarning(true);
        } else {
            doSaveApi();
        }
    };

    const cancelDuplicateDialog = () => {
        setIsDuplicateWarning(false);
    };

    const updateField = (field, value) => {
        setRequestData(prev => ({ ...prev, [field]: value }));
    };

    const addCompSet = () => {
        setRequestData(prev => {
            const isFirst = prev.comp_sets.length === 0;
            const newRow = {
                id: Date.now(),
                name: "",
                comp_set_id: "",
                is_primary: isFirst,
                is_active: true,
                deactivated_date: "",
                isNew: true,
            };
            return { ...prev, comp_sets: [...prev.comp_sets, newRow] };
        });
    };

    const updateCompSet = (index, field, value) => {
        const updated = requestData.comp_sets.map((row, i) => {
            if (field === "is_primary" && value === true) {
                const isPrimary = i === index;
                return { ...row, is_primary: isPrimary, ...(isPrimary ? { is_active: true, deactivated_date: "" } : {}) };
            }
            if (i !== index) return row;
            if (field === "is_active" && value === false) {
                const today = new Date().toISOString().split("T")[0];
                return { ...row, is_active: false, deactivated_date: today };
            }
            if (field === "is_active" && value === true) {
                return { ...row, is_active: true, deactivated_date: "" };
            }
            return { ...row, [field]: value };
        });
        setRequestData(prev => ({ ...prev, comp_sets: updated }));
    };

    const deleteCompSet = (index) => {
        const updated = requestData.comp_sets.filter((_, i) => i !== index);
        setRequestData(prev => ({ ...prev, comp_sets: updated }));
    };

    const fieldLabelStyle = {
        fontSize: 10,
        fontWeight: 600,
        color: "#606060",
        textTransform: "uppercase",
        letterSpacing: "0.06em",
        marginBottom: 4,
    };

    const sectionLabelStyle = {
        fontSize: 11,
        fontWeight: 700,
        color: "#606060",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
    };

    return (
        <div>
            {/* Top bar — Back to Dashboard + Save/Cancel */}
            <Box className="top-axn-bar-1">
                <Tooltip title="Back to Dashboard" followCursor>
                    <Button
                        size="medium"
                        variant="contained"
                        className="back-to-dash-btn"
                        onClick={goBackToDashboard}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16.001" height="16.001" viewBox="0 0 16.001 16.001">
                            <g id="Group_2234" data-name="Group 2234" transform="translate(24154 19449)">
                                <path id="Path_3278" data-name="Path 3278" d="M.889,0h8.3a.889.889,0,0,1,.889.889V9.482a.889.889,0,0,1-.889.889H.889A.889.889,0,0,1,0,9.482V.889A.889.889,0,0,1,.889,0Z" transform="translate(-24154 -19449)" fill="#606060" />
                                <g id="Path_3279" data-name="Path 3279" transform="translate(-24142.74 -19449)" fill="#333">
                                    <path d="M 3.852076530456543 4.341016292572021 L 0.8889365196228027 4.341016292572021 C 0.6193365454673767 4.341016292572021 0.3999965190887451 4.121676445007324 0.3999965190887451 3.852076530456543 L 0.3999965190887451 0.8889365196228027 C 0.3999965190887451 0.6193365454673767 0.6193365454673767 0.3999965190887451 0.8889365196228027 0.3999965190887451 L 3.852076530456543 0.3999965190887451 C 4.121676445007324 0.3999965190887451 4.341016292572021 0.6193365454673767 4.341016292572021 0.8889365196228027 L 4.341016292572021 3.852076530456543 C 4.341016292572021 4.121676445007324 4.121676445007324 4.341016292572021 3.852076530456543 4.341016292572021 Z" stroke="none" />
                                    <path d="M 0.8889365196228027 0.7999966144561768 C 0.8398964405059814 0.7999966144561768 0.7999966144561768 0.8398964405059814 0.7999966144561768 0.8889365196228027 L 0.7999966144561768 3.852076530456543 C 0.7999966144561768 3.901116609573364 0.8398964405059814 3.941016674041748 0.8889365196228027 3.941016674041748 L 3.852076530456543 3.941016674041748 C 3.901116609573364 3.941016674041748 3.941016674041748 3.901116609573364 3.941016674041748 3.852076530456543 L 3.941016674041748 0.8889365196228027 C 3.941016674041748 0.8398964405059814 3.901116609573364 0.7999966144561768 3.852076530456543 0.7999966144561768 L 0.8889365196228027 0.7999966144561768 M 0.8889365196228027 -3.337860107421875e-06 L 3.852076530456543 -3.337860107421875e-06 C 4.343016624450684 -3.337860107421875e-06 4.741016387939453 0.3979964256286621 4.741016387939453 0.8889365196228027 L 4.741016387939453 3.852076530456543 C 4.741016387939453 4.343016624450684 4.343016624450684 4.741016387939453 3.852076530456543 4.741016387939453 L 0.8889365196228027 4.741016437939453 C 0.3979964256286621 4.741016437939453 -3.337860107421875e-06 4.343016624450684 -3.337860107421875e-06 3.852076530456543 L -3.337860107421875e-06 0.8889365196228027 C -3.337860107421875e-06 0.3979964256286621 0.3979964256286621 -3.337860107421875e-06 0.8889365196228027 -3.337860107421875e-06 Z" stroke="none" fill="#333" />
                                </g>
                                <g id="Path_3280" data-name="Path 3280" transform="translate(-24142.74 -19443.371)" fill="#333">
                                    <path d="M 3.852076530456543 4.341016292572021 L 0.8889365196228027 4.341016292572021 C 0.6193365454673767 4.341016292572021 0.3999965190887451 4.121676445007324 0.3999965190887451 3.852076530456543 L 0.3999965190887451 0.8889365196228027 C 0.3999965190887451 0.6193365454673767 0.6193365454673767 0.3999965190887451 0.8889365196228027 0.3999965190887451 L 3.852076530456543 0.3999965190887451 C 4.121676445007324 0.3999965190887451 4.341016292572021 0.6193365454673767 4.341016292572021 0.8889365196228027 L 4.341016292572021 3.852076530456543 C 4.341016292572021 4.121676445007324 4.121676445007324 4.341016292572021 3.852076530456543 4.341016292572021 Z" stroke="none" />
                                    <path d="M 0.8889365196228027 0.7999966144561768 C 0.8398964405059814 0.7999966144561768 0.7999966144561768 0.8398964405059814 0.7999966144561768 0.8889365196228027 L 0.7999966144561768 3.852076530456543 C 0.7999966144561768 3.901116609573364 0.8398964405059814 3.941016674041748 0.8889365196228027 3.941016674041748 L 3.852076530456543 3.941016674041748 C 3.901116609573364 3.941016674041748 3.941016674041748 3.901116609573364 3.941016674041748 3.852076530456543 L 3.941016674041748 0.8889365196228027 C 3.941016674041748 0.8398964405059814 3.901116609573364 0.7999966144561768 3.852076530456543 0.7999966144561768 L 0.8889365196228027 0.7999966144561768 M 0.8889365196228027 -3.337860107421875e-06 L 3.852076530456543 -3.337860107421875e-06 C 4.343016624450684 -3.337860107421875e-06 4.741016387939453 0.3979964256286621 4.741016387939453 0.8889365196228027 L 4.741016387939453 3.852076530456543 C 4.741016387939453 4.343016624450684 4.343016624450684 4.741016387939453 3.852076530456543 4.741016387939453 L 0.8889365196228027 4.741016437939453 C 0.3979964256286621 4.741016437939453 -3.337860107421875e-06 4.343016624450684 -3.337860107421875e-06 3.852076530456543 L -3.337860107421875e-06 0.8889365196228027 C -3.337860107421875e-06 0.3979964256286621 0.3979964256286621 -3.337860107421875e-06 0.8889365196228027 -3.337860107421875e-06 Z" stroke="none" fill="#333" />
                                </g>
                                <g id="Path_3283" data-name="Path 3283" transform="translate(-24142.74 -19437.74)" fill="#333">
                                    <path d="M 3.852076530456543 4.341016292572021 L 0.8889365196228027 4.341016292572021 C 0.6193365454673767 4.341016292572021 0.3999965190887451 4.121676445007324 0.3999965190887451 3.852076530456543 L 0.3999965190887451 0.8889365196228027 C 0.3999965190887451 0.6193365454673767 0.6193365454673767 0.3999965190887451 0.8889365196228027 0.3999965190887451 L 3.852076530456543 0.3999965190887451 C 4.121676445007324 0.3999965190887451 4.341016292572021 0.6193365454673767 4.341016292572021 0.8889365196228027 L 4.341016292572021 3.852076530456543 C 4.341016292572021 4.121676445007324 4.121676445007324 4.341016292572021 3.852076530456543 4.341016292572021 Z" stroke="none" />
                                    <path d="M 0.8889365196228027 0.7999966144561768 C 0.8398964405059814 0.7999966144561768 0.7999966144561768 0.8398964405059814 0.7999966144561768 0.8889365196228027 L 0.7999966144561768 3.852076530456543 C 0.7999966144561768 3.901116609573364 0.8398964405059814 3.941016674041748 0.8889365196228027 3.941016674041748 L 3.852076530456543 3.941016674041748 C 3.901116609573364 3.941016674041748 3.941016674041748 3.901116609573364 3.941016674041748 3.852076530456543 L 3.941016674041748 0.8889365196228027 C 3.941016674041748 0.8398964405059814 3.901116609573364 0.7999966144561768 3.852076530456543 0.7999966144561768 L 0.8889365196228027 0.7999966144561768 M 0.8889365196228027 -3.337860107421875e-06 L 3.852076530456543 -3.337860107421875e-06 C 4.343016624450684 -3.337860107421875e-06 4.741016387939453 0.3979964256286621 4.741016387939453 0.8889365196228027 L 4.741016387939453 3.852076530456543 C 4.741016387939453 4.343016624450684 4.343016624450684 4.741016387939453 3.852076530456543 4.741016387939453 L 0.8889365196228027 4.741016437939453 C 0.3979964256286621 4.741016437939453 -3.337860107421875e-06 4.343016624450684 -3.337860107421875e-06 3.852076530456543 L -3.337860107421875e-06 0.8889365196228027 C -3.337860107421875e-06 0.3979964256286621 0.3979964256286621 -3.337860107421875e-06 0.8889365196228027 -3.337860107421875e-06 Z" stroke="none" fill="#333" />
                                </g>
                                <g id="Path_3281" data-name="Path 3281" transform="translate(-24154 -19437.74)" fill="#333">
                                    <path d="M 3.852076530456543 4.341016292572021 L 0.8889365196228027 4.341016292572021 C 0.6193365454673767 4.341016292572021 0.3999965190887451 4.121676445007324 0.3999965190887451 3.852076530456543 L 0.3999965190887451 0.8889365196228027 C 0.3999965190887451 0.6193365454673767 0.6193365454673767 0.3999965190887451 0.8889365196228027 0.3999965190887451 L 3.852076530456543 0.3999965190887451 C 4.121676445007324 0.3999965190887451 4.341016292572021 0.6193365454673767 4.341016292572021 0.8889365196228027 L 4.341016292572021 3.852076530456543 C 4.341016292572021 4.121676445007324 4.121676445007324 4.341016292572021 3.852076530456543 4.341016292572021 Z" stroke="none" />
                                    <path d="M 0.8889365196228027 0.7999966144561768 C 0.8398964405059814 0.7999966144561768 0.7999966144561768 0.8398964405059814 0.7999966144561768 0.8889365196228027 L 0.7999966144561768 3.852076530456543 C 0.7999966144561768 3.901116609573364 0.8398964405059814 3.941016674041748 0.8889365196228027 3.941016674041748 L 3.852076530456543 3.941016674041748 C 3.901116609573364 3.941016674041748 3.941016674041748 3.901116609573364 3.941016674041748 3.852076530456543 L 3.941016674041748 0.8889365196228027 C 3.941016674041748 0.8398964405059814 3.901116609573364 0.7999966144561768 3.852076530456543 0.7999966144561768 L 0.8889365196228027 0.7999966144561768 M 0.8889365196228027 -3.337860107421875e-06 L 3.852076530456543 -3.337860107421875e-06 C 4.343016624450684 -3.337860107421875e-06 4.741016387939453 0.3979964256286621 4.741016387939453 0.8889365196228027 L 4.741016387939453 3.852076530456543 C 4.741016387939453 4.343016624450684 4.343016624450684 4.741016387939453 3.852076530456543 4.741016387939453 L 0.8889365196228027 4.741016437939453 C 0.3979964256286621 4.741016437939453 -3.337860107421875e-06 4.343016624450684 -3.337860107421875e-06 3.852076530456543 L -3.337860107421875e-06 0.8889365196228027 C -3.337860107421875e-06 0.3979964256286621 0.3979964256286621 -3.337860107421875e-06 0.8889365196228027 -3.337860107421875e-06 Z" stroke="none" fill="#333" />
                                </g>
                                <g id="Path_3282" data-name="Path 3282" transform="translate(-24148.369 -19437.74)" fill="#333">
                                    <path d="M 3.852076530456543 4.341016292572021 L 0.8889365196228027 4.341016292572021 C 0.6193365454673767 4.341016292572021 0.3999965190887451 4.121676445007324 0.3999965190887451 3.852076530456543 L 0.3999965190887451 0.8889365196228027 C 0.3999965190887451 0.6193365454673767 0.6193365454673767 0.3999965190887451 0.8889365196228027 0.3999965190887451 L 3.852076530456543 0.3999965190887451 C 4.121676445007324 0.3999965190887451 4.341016292572021 0.6193365454673767 4.341016292572021 0.8889365196228027 L 4.341016292572021 3.852076530456543 C 4.341016292572021 4.121676445007324 4.121676445007324 4.341016292572021 3.852076530456543 4.341016292572021 Z" stroke="none" />
                                    <path d="M 0.8889365196228027 0.7999966144561768 C 0.8398964405059814 0.7999966144561768 0.7999966144561768 0.8398964405059814 0.7999966144561768 0.8889365196228027 L 0.7999966144561768 3.852076530456543 C 0.7999966144561768 3.901116609573364 0.8398964405059814 3.941016674041748 0.8889365196228027 3.941016674041748 L 3.852076530456543 3.941016674041748 C 3.901116609573364 3.941016674041748 3.941016674041748 3.901116609573364 3.941016674041748 3.852076530456543 L 3.941016674041748 0.8889365196228027 C 3.941016674041748 0.8398964405059814 3.901116609573364 0.7999966144561768 3.852076530456543 0.7999966144561768 L 0.8889365196228027 0.7999966144561768 M 0.8889365196228027 -3.337860107421875e-06 L 3.852076530456543 -3.337860107421875e-06 C 4.343016624450684 -3.337860107421875e-06 4.741016387939453 0.3979964256286621 4.741016387939453 0.8889365196228027 L 4.741016387939453 3.852076530456543 C 4.741016387939453 4.343016624450684 4.343016624450684 4.741016387939453 3.852076530456543 4.741016387939453 L 0.8889365196228027 4.741016437939453 C 0.3979964256286621 4.741016437939453 -3.337860107421875e-06 4.343016624450684 -3.337860107421875e-06 3.852076530456543 L -3.337860107421875e-06 0.8889365196228027 C -3.337860107421875e-06 0.3979964256286621 0.3979964256286621 -3.337860107421875e-06 0.8889365196228027 -3.337860107421875e-06 Z" stroke="none" fill="#333" />
                                </g>
                            </g>
                        </svg>
                        <span className="back-to-dash-txt">Back to Dashboard</span>
                    </Button>
                </Tooltip>

                <Box sx={{ flex: 1, display: "flex", justifyContent: "center", px: 2 }}>
                    {isRequestSuccess && (
                        <Alert severity="success" sx={{ py: 0, fontSize: 12, alignItems: "center" }}>
                            Settings saved successfully!
                        </Alert>
                    )}
                    {isRequestError && (
                        <Alert severity="error" sx={{ py: 0, fontSize: 12, alignItems: "center" }}>
                            {resErrorMsg}
                        </Alert>
                    )}
                    {!isRequestSuccess && !isRequestError && submitAttempted && hasErrors && (
                        <Alert severity="error" sx={{ py: 0, fontSize: 12, alignItems: "center" }}>
                            Please fill in all required fields before saving.
                        </Alert>
                    )}
                </Box>

                <Box className={isShakeButton ? "top-axn-bar-1-sub bt-shake" : "top-axn-bar-1-sub"}>
                    <Button
                        disabled={!isDirty}
                        onClick={cancelSave}
                        variant="outlined"
                        className={isDirty ? "navigate-cancel-button btn-active" : "navigate-cancel-button"}
                        size="small"
                        sx={{ minWidth: "auto" }}
                    >
                        cancel
                    </Button>
                    {usrReadyOnlyStatus ? (
                        <Tooltip title={getPermissionMessage(userLoginType)} followCursor>
                            <Button
                                variant="outlined"
                                size="small"
                                className="navigate-save-button"
                                sx={{ minWidth: "auto", mr: 1 }}
                            >
                                <span>Save</span>
                            </Button>
                        </Tooltip>
                    ) : (
                        <Button
                            disabled={!isDirty}
                            onClick={saveChanges}
                            variant="outlined"
                            size="small"
                            className={isDirty ? "navigate-save-button btn-active" : "navigate-save-button"}
                            sx={{ minWidth: "auto", mr: 1 }}
                        >
                            <span style={{ display: !isrequesting ? "block" : "none" }}>Save</span>
                            <span style={{ display: isrequesting ? "block" : "none", paddingRight: 10 }}>saving...</span>
                            <CircularProgress sx={{ display: isrequesting ? "block" : "none" }} color="white" size="16px" />
                        </Button>
                    )}
                </Box>
            </Box>

            {/* Breadcrumb Bar */}
            <Box className="top-axn-bar">
                <Box className="breadcrumb" sx={{ color: "white.main" }}>
                    <span className="breadcrum-inactive">Settings/</span>
                    <span className="breadcrum-inactive">Property Settings/</span>
                    <span className="breadcrum-inactive">Benchmarking/</span>
                    <span className="breadcrum-active">Amadeus Demand 360</span>
                </Box>
            </Box>

            {/* Main Content */}
            <Box className="bgwhite main-scroll-wrap" sx={{ pl: 2, pr: 2 }}>
                {isLoading && (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 6 }}>
                        <CircularProgress size={28} sx={{ color: "#00415F" }} />
                    </Box>
                )}
                {!isLoading && (<>
                <div className="profile-heading" style={{ paddingBottom: 4 }}>Amadeus Demand 360</div>
                <p style={{ fontSize: 13, color: "#606060", margin: "0 0 16px 0", lineHeight: 1.5 }}>
                    Map your Amadeus Demand 360 subscription so LodgIQ Advance BI attributes feed data to this property and powers the Benchmark report.
                </p>

                <Alert severity="error" className="save-changes-alert display-none" sx={{ mb: 1 }}>
                    Please save or cancel your changes before navigating away.
                </Alert>

                {/* Main Card */}
                <Box sx={{ border: "1px solid #e0e0e0", borderRadius: "8px", overflow: "hidden", mb: 2 }}>

                    {/* Card Header */}
                    <Box sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        px: 2,
                        py: 1.5,
                        borderBottom: "1px solid #e0e0e0",
                        background: "#e4edf1",
                    }}>
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 2L2 7l10 5 10-5-10-5z" fill="#00415F" opacity="0.7" />
                            <path d="M2 17l10 5 10-5" stroke="#00415F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                            <path d="M2 12l10 5 10-5" stroke="#00415F" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                        <span style={{ fontSize: 13, fontWeight: 600, color: "#333333" }}>Amadeus Demand 360</span>
                    </Box>

                    {/* Card Body */}
                    <Box sx={{ p: 2.5 }}>

                        {/* ── Subscription Mapping ── */}
                        <div style={sectionLabelStyle}>Subscription Mapping</div>
                        <Grid container spacing={2} sx={{ mt: 0.5, mb: 2 }}>
                            <Grid item xs={12} sm={6} md={3}>
                                <div style={fieldLabelStyle}>
                                    Subscription ID&nbsp;<span style={{ color: "#e03131" }}>*</span>
                                </div>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="e.g. 1001457"
                                    value={requestData.subscription_id}
                                    error={getFieldError(requestData.subscription_id)}
                                    disabled={usrReadyOnlyStatus}
                                    onChange={(e) => updateField("subscription_id", e.target.value.replace(/[^0-9]/g, ""))}
                                    inputProps={{ style: { fontSize: 13 } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <div style={fieldLabelStyle}>
                                    Subscription Property ID&nbsp;<span style={{ color: "#e03131" }}>*</span>
                                </div>
                                <TextField
                                    fullWidth
                                    size="small"
                                    placeholder="e.g. 118"
                                    value={requestData.subscription_property_id}
                                    error={getFieldError(requestData.subscription_property_id)}
                                    disabled={usrReadyOnlyStatus}
                                    onChange={(e) => updateField("subscription_property_id", e.target.value.replace(/[^0-9]/g, ""))}
                                    inputProps={{ style: { fontSize: 13 } }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <div style={fieldLabelStyle}>
                                    Currency Code&nbsp;<span style={{ color: "#e03131" }}>*</span>
                                </div>
                                <FormControl fullWidth size="small" error={getFieldError(requestData.currency_code)} disabled={usrReadyOnlyStatus}>
                                    <Select
                                        value={requestData.currency_code}
                                        onChange={(e) => updateField("currency_code", e.target.value)}
                                        displayEmpty
                                        sx={{ fontSize: 13 }}
                                    >
                                        <MenuItem value="" disabled>Select currency</MenuItem>
                                        {CURRENCY_OPTIONS.map(c => (
                                            <MenuItem key={c} value={c}>{c}</MenuItem>
                                        ))}
                                    </Select>
                                    {getFieldError(requestData.currency_code) && <FormHelperText>Required</FormHelperText>}
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6} md={3}>
                                <div style={fieldLabelStyle}>
                                    Delivery Frequency&nbsp;
                                    <span style={{ fontWeight: 400, textTransform: "none", color: "#999" }}>· optional</span>
                                </div>
                                <FormControl fullWidth size="small" disabled={usrReadyOnlyStatus}>
                                    <Select
                                        value={requestData.delivery_frequency}
                                        onChange={(e) => updateField("delivery_frequency", e.target.value)}
                                        displayEmpty
                                        sx={{ fontSize: 13 }}
                                    >
                                        <MenuItem value="">---</MenuItem>
                                        {FREQUENCY_OPTIONS.map(f => (
                                            <MenuItem key={f} value={f}>{f}</MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        {/* ── Compsets Table ── */}
                        <Box sx={{
                            mt: 2,
                            border: "1px solid #dceef5",
                            borderRadius: "10px",
                            overflow: "hidden",
                        }}>
                            {/* Toolbar */}
                            <Box sx={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                px: 2,
                                py: 1.2,
                                background: "linear-gradient(90deg, #e8f4f8 0%, #ddeef5 100%)",
                            }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
                                        <path d="M3 6h18M3 12h18M3 18h18" stroke="#00415F" strokeWidth="2" strokeLinecap="round" />
                                    </svg>
                                    <Box>
                                        <span style={{ fontSize: 12, fontWeight: 700, color: "#00415F", textTransform: "uppercase", letterSpacing: "0.07em" }}>
                                            Compsets
                                        </span>
                                        <span style={{ fontSize: 11, color: "#7aa5b8", marginLeft: 8 }}>
                                            {requestData.comp_sets.length === 0
                                                ? "No compsets added yet"
                                                : `${requestData.comp_sets.length} total · ${requestData.comp_sets.filter(c => c.is_active).length} active`}
                                        </span>
                                    </Box>
                                </Box>
                                <Button
                                    variant="outlined"
                                    size="small"
                                    disabled={usrReadyOnlyStatus}
                                    onClick={addCompSet}
                                    sx={{
                                        fontSize: 11,
                                        textTransform: "none",
                                        borderColor: "#00415F",
                                        color: "#00415F",
                                        px: 1.5,
                                        py: "4px",
                                        "&:hover": { borderColor: "#00415F", background: "rgba(0,65,95,0.06)" },
                                        "&.Mui-disabled": { borderColor: "#b0cdd8", color: "#b0cdd8" },
                                    }}
                                >
                                    + Add Compset
                                </Button>
                            </Box>

                            {/* Column Headers */}
                            <Grid
                                container
                                alignItems="center"
                                sx={{
                                    fontSize: 10,
                                    fontWeight: 700,
                                    color: "#606060",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.06em",
                                    background: "#f4f8fa",
                                    borderBottom: "1px solid #dceef5",
                                    px: 1.5,
                                    py: 1,
                                }}
                            >
                                <Grid item md={3} xs={3}>Compset Name</Grid>
                                <Grid item md={2.5} xs={2.5}>
                                    Compset ID&nbsp;<span style={{ color: "#e03131" }}>*</span>
                                </Grid>
                                <Grid item md={1.5} xs={1.5} sx={{ textAlign: "center" }}>Primary</Grid>
                                <Grid item md={2} xs={2} sx={{ textAlign: "center" }}>Status</Grid>
                                <Grid item md={2} xs={2} sx={{ textAlign: "center" }}>Deactivated</Grid>
                                <Grid item md={1} xs={1} />
                            </Grid>

                        {/* Table Rows */}
                        {requestData.comp_sets.map((row, index) => (
                            <Box
                                key={row.id}
                                sx={{
                                    border: "1px solid #e8eaed",
                                    borderTop: "none",
                                    borderLeft: `3px solid ${row.is_primary ? "#00415F" : "transparent"}`,
                                    background: row.is_active ? "#fff" : "#fafafa",
                                    transition: "box-shadow 0.15s ease, border-left-color 0.15s ease",
                                    "&:hover": { boxShadow: "0 2px 8px rgba(0,0,0,0.07)" },
                                    "&:last-of-type": { borderRadius: "0 0 8px 8px" },
                                }}
                            >
                                <Grid
                                    container
                                    alignItems="center"
                                    sx={{ px: 1.5, py: 1, opacity: row.is_active ? 1 : 0.65 }}
                                >
                                    <Grid item md={3} xs={3} sx={{ pr: 1 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="Compset Name"
                                            value={row.name}
                                            disabled={usrReadyOnlyStatus}
                                            onChange={(e) => updateCompSet(index, "name", e.target.value)}
                                            inputProps={{
                                                style: { fontSize: 13 },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={2.5} xs={2.5} sx={{ pr: 1 }}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            placeholder="e.g. 1001"
                                            value={row.comp_set_id}
                                            error={getCompSetIdError(row.comp_set_id)}
                                            disabled={usrReadyOnlyStatus}
                                            onChange={(e) => updateCompSet(index, "comp_set_id", e.target.value.replace(/[^0-9]/g, ""))}
                                            inputProps={{ style: { fontSize: 13 } }}
                                        />
                                    </Grid>
                                    <Grid item md={1.5} xs={1.5} sx={{ textAlign: "center" }}>
                                        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: "2px" }}>
                                            <Radio
                                                size="small"
                                                checked={row.is_primary}
                                                disabled={usrReadyOnlyStatus}
                                                onChange={() => updateCompSet(index, "is_primary", true)}
                                                sx={{ p: "2px", "&.Mui-checked": { color: "#00415F" } }}
                                            />
                                        </Box>
                                    </Grid>
                                    <Grid item md={2} xs={2} sx={{ textAlign: "center" }}>
                                        <Switch
                                            size="small"
                                            checked={row.is_active}
                                            disabled={row.is_primary || usrReadyOnlyStatus}
                                            onChange={(e) => updateCompSet(index, "is_active", e.target.checked)}
                                            sx={{
                                                "& .MuiSwitch-switchBase.Mui-checked": { color: "#00415F" },
                                                "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { backgroundColor: "#00415F" },
                                            }}
                                        />
                                    </Grid>
                                    <Grid item md={2} xs={2} sx={{ textAlign: "center" }}>
                                        {!row.is_active && row.deactivated_date ? (
                                            <span style={{
                                                fontSize: 12,
                                                color: "#e03131",
                                                fontWeight: 500,
                                                background: "#fff5f5",
                                                border: "1px solid #fcc",
                                                borderRadius: 4,
                                                padding: "3px 8px",
                                                display: "inline-block",
                                            }}>
                                                {row.deactivated_date}
                                            </span>
                                        ) : (
                                            <span style={{ fontSize: 13, color: "#ccc" }}>—</span>
                                        )}
                                    </Grid>
                                    <Grid item md={1} xs={1} sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: "2px" }}>
                                        {row.isNew && (
                                            <Tooltip
                                                title={usrReadyOnlyStatus ? getPermissionMessage(userLoginType) : "Remove"}
                                                followCursor
                                            >
                                                <span>
                                                    <Button
                                                        size="small"
                                                        disabled={usrReadyOnlyStatus}
                                                        onClick={() => deleteCompSet(index)}
                                                        sx={{ minWidth: "auto", p: "3px", color: "#bbb", "&:hover": { color: "#e03131", background: "#fff5f5" }, "&.Mui-disabled": { color: "#e0e0e0" } }}
                                                    >
                                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                            <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                                            <path d="M8 6V4h8v2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                            <path d="M19 6l-1 14H6L5 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                                            <path d="M10 11v6M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                                                        </svg>
                                                    </Button>
                                                </span>
                                            </Tooltip>
                                        )}
                                        {usrReadyOnlyStatus && (
                                            <Button
                                                size="small"
                                                sx={{ minWidth: "auto", p: "3px", color: "#ccc", cursor: "default" }}
                                            >
                                                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                                                    <rect x="5" y="11" width="14" height="10" rx="2" stroke="currentColor" strokeWidth="2" />
                                                    <path d="M8 11V7a4 4 0 0 1 8 0v4" stroke="currentColor" strokeWidth="2" />
                                                </svg>
                                            </Button>
                                        )}
                                    </Grid>
                                </Grid>
                            </Box>
                        ))}

                        {requestData.comp_sets.length === 0 && (
                            <Box sx={{
                                textAlign: "center",
                                py: 4,
                                fontSize: 13,
                                background: submitAttempted ? "#fff5f5" : "#fafcfe",
                                color: submitAttempted ? "#e03131" : "#aaa",
                            }}>
                                {submitAttempted
                                    ? "At least one compset is required to save."
                                    : "No compsets added yet. Click \"+ Add Compset\" to add one."}
                            </Box>
                        )}

                        </Box>{/* end compsets table */}

                    </Box>
                </Box>
            </>)}
            </Box>

            {/* Duplicate Comp Set ID warning dialog */}
            <Dialog open={isDuplicateWarning} onClose={cancelDuplicateDialog} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontSize: 15, fontWeight: 700, pb: 1 }}>
                    Duplicate Comp Set ID
                </DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ fontSize: 13 }}>
                        One or more Comp Set IDs are duplicated. Do you want to continue saving anyway?
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button
                        variant="outlined"
                        size="small"
                        onClick={cancelDuplicateDialog}
                        sx={{ textTransform: "none", fontSize: 13 }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        size="small"
                        onClick={doSaveApi}
                        sx={{ textTransform: "none", fontSize: 13, background: "#00415F", "&:hover": { background: "#003049" } }}
                    >
                        Continue
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
}

export default AmadeusSettings;
